import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import BrandedButton from '@/components/buttons/BrandedButton';
import GoogleAutoComplete from '@/components/inputs/GoogleAutocomplete';
import PhoneInput from '@/components/inputs/PhoneInput';
import RadioButtonGroup from '@/components/inputs/RadioButtonGroup';
import TextInputField from '@/components/inputs/TextInputField';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import Text from '@/components/Text';
import {
  CompanyOwnershipTypeEnum,
  GeneralData,
  generalResolver,
  organizationCategoriesOptions
} from '@/lib/validations/NpoPaymentOnboarding';
import { parseAddressComponents } from '@/utils/addressFromAddressComponents';
import { i18n } from '@/utils/i18n';

interface GeneralFormProps {
  initialData?: GeneralData;
  onDone?: (data: GeneralData) => void;
  onSaveAndContinue?: (data: GeneralData) => void;
  doneLabel?: string;
  isLoading?: boolean;
}

const GeneralForm = ({
  initialData,
  onDone,
  onSaveAndContinue,
  doneLabel = i18n.t('mix.done'),
  isLoading = false
}: GeneralFormProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    resetField,
    setError,
    setFocus,
    reset,
    trigger,
    formState: { isValid, errors, isDirty }
  } = useForm<GeneralData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: generalResolver
  });

  const onSubmit: SubmitHandler<GeneralData> = async (data) => {
    if (onDone) {
      onDone(data);
    }
  };

  const onSaveAndContinueHandler: SubmitHandler<GeneralData> = async (data) => {
    if (onSaveAndContinue) {
      onSaveAndContinue(data);
    }
  };

  useEffect(() => {
    if (initialData && !isDirty) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.formContainer}>
      <View style={styles.inputsContainer}>
        <TextInputField
          name="companyLegalName"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.general_form.companyLegalName_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          label={i18n.t('npoPaymentOnboarding.general_form.companyLegalName_label')}
          onSubmitEditing={() => setFocus('companyDbaName')}
        />
        <TextInputField
          name="companyDbaName"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.general_form.companyDbaName_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          label={i18n.t('npoPaymentOnboarding.general_form.companyDbaName_label')}
          onSubmitEditing={() => setFocus('companyOwnershipType')}
        />

        <View style={styles.legalAddress}>
          <GoogleAutoComplete
            initialValue={initialData?.legalAddress}
            type="address"
            predictionsScrollEnabled={false}
            error={errors.legalAddress}
            label={i18n.t('npoPaymentOnboarding.general_form.legalAddress_label')}
            placeholder={i18n.t('npoPaymentOnboarding.general_form.legalAddress_placeholder')}
            onChange={(result) => {
              if (!result?.postalCode || result.postalCode === '' || !result.city || result.city === '') {
                setError('legalAddress', {
                  message: i18n.t('mix.enter_valid_address')
                });
              } else if (result) {
                const components = parseAddressComponents(result.addressComponents);
                setValue(
                  'legalAddress',
                  `${components.street}, ${components.city}, ${components.state} ${components.postalCode}, ${components.country}`
                );
                trigger('legalAddress');
              } else {
                resetField('legalAddress');
                setError('legalAddress', {
                  type: 'required',
                  message: i18n.t('npoPaymentOnboarding.general_form.legalAddress_required')
                });
              }
            }}
          />
        </View>

        <View style={styles.dbaAddress}>
          <GoogleAutoComplete
            initialValue={initialData?.dbaAddress}
            type="address"
            predictionsScrollEnabled={false}
            error={errors.dbaAddress}
            label={i18n.t('npoPaymentOnboarding.general_form.dbaAddress_label')}
            placeholder={i18n.t('npoPaymentOnboarding.general_form.dbaAddress_placeholder')}
            onChange={(result) => {
              if (!result?.postalCode || result.postalCode === '' || !result.city || result.city === '') {
                setError('dbaAddress', {
                  message: i18n.t('mix.enter_valid_address')
                });
              } else if (result) {
                const components = parseAddressComponents(result.addressComponents);
                setValue(
                  'dbaAddress',
                  `${components.street}, ${components.city}, ${components.state} ${components.postalCode}, ${components.country}`
                );
                trigger('dbaAddress');
              } else {
                resetField('dbaAddress');
                setError('dbaAddress', {
                  type: 'required',
                  message: i18n.t('npoPaymentOnboarding.general_form.dbaAddress_required')
                });
              }
            }}
          />
        </View>

        <RadioButtonGroup
          groupLabel={
            <Text fontWeight="700" style={styles.radioButtonGroupLabel}>
              {i18n.t('npoPaymentOnboarding.general_form.companyOwnershipType_label')}
            </Text>
          }
          labelMargin={5}
          showNoneOption={false}
          control={control}
          name="companyOwnershipType"
          options={Object.values(CompanyOwnershipTypeEnum).map((type) => ({ label: type, value: type }))}
        />

        <RadioButtonGroup
          groupLabel={
            <Text fontWeight="700" style={styles.radioButtonGroupLabel}>
              {i18n.t('npoPaymentOnboarding.general_form.organizationCategory_label')}
            </Text>
          }
          labelMargin={5}
          showNoneOption={false}
          control={control}
          name="organizationCategory"
          options={organizationCategoriesOptions}
        />

        <TextInputField
          name="companyFederalTaxId"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.general_form.companyFederalTaxId_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          label={i18n.t('npoPaymentOnboarding.general_form.companyFederalTaxId_label')}
          onSubmitEditing={() => setFocus('companyUrl')}
        />

        <TextInputField
          name="companyUrl"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.general_form.companyUrl_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          label={i18n.t('npoPaymentOnboarding.general_form.companyUrl_label')}
          autoCapitalize="none"
          onSubmitEditing={() => setFocus('customerSupportNumber')}
        />

        {/* <TextInputField
          name="companyMcc"
          control={control}
          placeholder={i18n.t('npoPaymentOnboarding.general_form.companyMcc_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          label={i18n.t('npoPaymentOnboarding.general_form.companyMcc_label')}
          onSubmitEditing={() => setFocus('customerSupportNumber')}
        /> */}

        <View style={styles.phoneInputContainer1}>
          <PhoneInput
            initialValue={initialData?.customerSupportNumber}
            label={i18n.t('npoPaymentOnboarding.general_form.customerSupportNumber_label')}
            placeholder={i18n.t('npoPaymentOnboarding.general_form.customerSupportNumber_placeholder')}
            error={errors.customerSupportNumber}
            onNumberChange={(number) => {
              if (number) {
                const fullNumber = `${number?.getNationalNumber()}`;
                setValue('customerSupportNumber', fullNumber);
                trigger('customerSupportNumber');
              } else {
                resetField('customerSupportNumber');
                setError('customerSupportNumber', {
                  type: 'required',
                  message: i18n.t('mix.valid_phone_number')
                });
              }
            }}
          />
        </View>

        <View style={styles.phoneInputContainer2}>
          <PhoneInput
            initialValue={initialData?.companyPhoneNumber}
            label={i18n.t('npoPaymentOnboarding.general_form.companyPhoneNumber_label')}
            placeholder={i18n.t('npoPaymentOnboarding.general_form.companyPhoneNumber_placeholder')}
            error={errors.companyPhoneNumber}
            onNumberChange={(number) => {
              if (number) {
                const fullNumber = `${number?.getNationalNumber()}`;
                setValue('companyPhoneNumber', fullNumber);
                trigger('companyPhoneNumber');
              } else {
                resetField('companyPhoneNumber');
                setError('companyPhoneNumber', {
                  type: 'required',
                  message: i18n.t('mix.valid_phone_number')
                });
              }
            }}
          />
        </View>
      </View>
      {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

      <BrandedButton style={styles.button} loading={isLoading} onPress={handleSubmit(onSubmit)} disabled={!isValid}>
        {doneLabel}
      </BrandedButton>
      <SaveAndContinueLaterButton onPress={handleSubmit(onSaveAndContinueHandler)} />
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    justifyContent: 'space-between'
  },

  inputsContainer: {
    gap: 15
  },

  radioButtonGroupLabel: {
    color: 'black'
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  button: {
    zIndex: 1,
    marginTop: 15
  },

  legalAddress: {
    zIndex: 9999
  },

  dbaAddress: {
    zIndex: 999
  },

  phoneInputContainer1: {
    zIndex: 10
  },

  phoneInputContainer2: {
    zIndex: 5
  }
});

export default GeneralForm;
